import { ActionIcon, Group, Menu, Tooltip } from "@mantine/core";
import { IconPlus, IconX } from "@tabler/icons-react";
import {
  EdgeLabelRenderer,
  getBezierPath,
  getSmoothStepPath,
  useReactFlow,
} from "reactflow";
import { defaultNodeConfigs, isMainAction, nodeWidth } from "../flowSettings";
import { useMemo, useState } from "react";
import { useFlowStore } from "../../../../../../stores/WorkflowStore";
import { handleLayout } from "../layouter";
import ActionsList from "../components/ActionsList";
import { modals } from "@mantine/modals";
import AddStep from "../nodes/AddStep";
import { getInheritedCredentials } from "../flowUtils";
import { useLanguage } from "../../../../../../stores/LanguageStore";

function getPath({
  sourceX,
  sourceY,
  sourcePosition,
  targetX,
  targetY,
  targetPosition,
}) {
  return getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });
}

const FlowEdge = ({
  sourceX,
  sourceY,
  targetX,
  targetY,
  target,
  source,
  sourcePosition,
  targetPosition,
  data,
  markerEnd,
}) => {
  const reactFlow = useReactFlow();
  const nodes = useFlowStore((s) => s.nodes);
  const linking = useFlowStore((s) => s.linking);
  const edgeId = `e-${source}-${target}`;
  const lang = useLanguage((s) => s.language);

  const targetHasMultipleSources = useMemo(() => {
    let targetNode = nodes.find((x) => x.id === target);

    if (targetNode) {
      return Array.isArray(targetNode?.data?.source);
    }
    return false;
  }, [nodes, target]);

  const [edgePath, labelX, labelY] = getPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const handleAdd = (actionType) => {
    modals.closeAll();
    const getId = useFlowStore.getState().getId;
    const setNodes = useFlowStore.getState().setNodes;
    const updateNodeData = useFlowStore.getState().updateNodeData;
    const edges = useFlowStore.getState().edges;
    const setEdges = useFlowStore.getState().setEdges;
    const initialId = useFlowStore.getState().initialId;
    const targetNode = nodes.find((x) => x.id === target);

    let newNodes = [...nodes];
    let newEdges = [...edges.filter((x) => x.id !== edgeId)];

    let newEventNodeId = getId().toString();
    let newNodeId = getId().toString();

    //#region Choose automatically the same sender as initial action

    let autoData = { ...defaultNodeConfigs };
    const inheritedCreds = getInheritedCredentials(
      nodes,
      data?.source,
      actionType,
    );

    if (inheritedCreds) {
      autoData.integration_id = inheritedCreds.integration_id;
      autoData.pool_id = inheritedCreds.pool_id;
      autoData.signature_id = inheritedCreds.signature_id;
    }
    //#endregion

    const newEventNode = {
      id: newEventNodeId,
      type: "action",
      data: {
        actionType: "event",
        source: source === initialId || data?.fromEvent ? newNodeId : source,
        error: data?.error,
      },
      position: { x: sourceX + 64, y: sourceY - 128 },
    };
    newNodes.push(newEventNode);

    const newNode = {
      id: newNodeId,
      type: "action",
      data: {
        actionType,
        source:
          source === initialId
            ? initialId
            : data?.fromEvent
              ? source
              : newEventNodeId,
        error: data?.error,
        ...autoData,
      },
      position: { x: sourceX + 128, y: sourceY - 128 },
    };
    newNodes.push(newNode);

    // Source to event OR event to target
    let newEdge1 = {};
    if (source === initialId) {
      if (targetNode?.data?.actionType !== "event") {
        newEdge1 = {
          id: `e-${initialId}-${newNodeId}`,
          source: initialId,
          target: newNodeId,
          data: { fromEvent: false },
        };
      } else {
        newEdge1 = {
          id: `e-${initialId}-${newEventNodeId}`,
          source: initialId,
          target: newEventNodeId,
          data: { fromEvent: false },
        };

        newNode.data.source = newEventNodeId;
      }
    } else {
      newEdge1 = {
        id: `e-${source}-${data?.fromEvent ? newNodeId : newEventNodeId}`,
        source: source,
        target: data?.fromEvent ? newNodeId : newEventNodeId,
        data: { fromEvent: data?.fromEvent },
      };
    }
    newEdges.push(newEdge1);

    // Event to new Node
    let newEdge2 = {};
    if (source === initialId) {
      if (targetNode?.data?.actionType !== "event") {
        newEdge2 = {
          id: `e-${newNodeId}-${newEventNodeId}`,
          source: newNodeId,
          target: newEventNodeId,
          data: { fromEvent: false },
        };
      } else {
        newEdge2 = {
          id: `e-${newEventNodeId}-${newNodeId}`,
          source: newEventNodeId,
          target: newNodeId,
          data: { fromEvent: true },
        };
        newEventNode.data.source = initialId;
      }
    } else {
      newEdge2 = {
        id: `e-${data?.fromEvent ? newNodeId : newEventNodeId}-${
          data?.fromEvent ? newEventNodeId : newNodeId
        }`,
        source: data?.fromEvent ? newNodeId : newEventNodeId,
        target: data?.fromEvent ? newEventNodeId : newNodeId,
        data: { fromEvent: !data?.fromEvent },
      };
    }
    newEdges.push(newEdge2);

    // New node to Target OR new node to event if on initial
    let newEdge3 = {};
    if (source === initialId) {
      if (targetNode?.data?.actionType !== "event") {
        newEdge3 = {
          id: `e-${newEventNodeId}-${target}`,
          source: newEventNodeId,
          target: target,
          data: { fromEvent: true },
        };
      } else {
        newEdge3 = {
          id: `e-${newNodeId}-${target}`,
          source: newNodeId,
          target: target,
          data: { fromEvent: false },
        };
      }
    } else {
      newEdge3 = {
        id: `e-${data?.fromEvent ? newEventNodeId : newNodeId}-${target}`,
        source: data?.fromEvent ? newEventNodeId : newNodeId,
        target: target,
        data: { fromEvent: data?.fromEvent },
      };
    }
    newEdges.push(newEdge3);

    setNodes(newNodes);
    setEdges(newEdges);

    updateNodeData(target, {
      source:
        source === initialId
          ? targetNode?.data?.actionType === "event"
            ? newNodeId
            : newEventNodeId
          : data?.fromEvent
            ? newEventNodeId
            : newNodeId,
    });

    setTimeout(() => {
      handleLayout();
      // .then(r => {
      //   let newNodesResponse = r.nodes
      //   let n = newNodesResponse.find((x) => x.id === newNodeId);
      //   reactFlow?.setCenter(n.position?.x + 256, n.position?.y + 128, {
      //     duration: 1000,
      //     zoom: 1,
      //   });
      // });
    }, 50);
  };

  const handleDelete = () => {
    const updateNodeData = useFlowStore.getState().updateNodeData;
    const edges = useFlowStore.getState().edges;
    const setEdges = useFlowStore.getState().setEdges;

    let targetNode = nodes.find((x) => x.id === target);

    let newTargetSource = targetNode.data.source?.filter((x) => x !== source);

    updateNodeData(target, {
      source:
        newTargetSource.length === 1 ? newTargetSource[0] : newTargetSource,
    });

    setEdges(edges.filter((x) => x.id !== edgeId));
    setTimeout(() => {
      handleLayout();
    }, 10);
  };

  const handleClick = () => {
    modals.open({
      children: <AddStep handleActionClick={handleAdd} noEvent />,
      size: 1024,
    });
  };

  return (
    <>
      <path
        id={edgeId}
        className={"react-flow__edge-path"}
        d={edgePath}
        markerEnd={markerEnd}
        style={{
          stroke: "var(--mantine-color-primary-6)",
          strokeWidth: 1.4,
          strokeDasharray: "3",
        }}
      />

      {targetHasMultipleSources && !linking && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: "absolute",
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              borderRadius: "50%",
              fontWeight: 700,
              pointerEvents: "all",
            }}
            className="nodrag nopan"
          >
            <Group w={nodeWidth} justify="center">
              <ActionIcon
                radius={"lg"}
                size={"sm"}
                variant="white"
                style={{ boxShadow: "#00000033 0 1px 3px", border: "none" }}
                onClick={handleDelete}
                color="red"
              >
                <IconX size={16} />
              </ActionIcon>
            </Group>
          </div>
        </EdgeLabelRenderer>
      )}

      {!linking && !targetHasMultipleSources && (
        <EdgeLabelRenderer>
          <Tooltip label={lang.workflows.add_step}>
            <ActionIcon
              radius={"lg"}
              size={24}
              variant="filled"
              style={{
                position: "absolute",
                transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
                borderRadius: "50%",
                fontWeight: 700,
                pointerEvents: "all",
                border: "1px solid #0000000f",
                color: "var(--mantine-color-primary-6)",
              }}
              className="nodrag nopan node-plus edge"
              onClick={handleClick}
              // bg={"primary"}
            >
              <IconPlus size={18} />
            </ActionIcon>
          </Tooltip>
        </EdgeLabelRenderer>
      )}
    </>
  );
};
export default FlowEdge;
